import dayjs from 'dayjs'

import { formatDateRangeShort } from '../formatters/date.formatter'

export const shortcutsDatePickerOptions = () => {
  return [
    {
      text: 'This year',
      value: [dayjs().startOf('year').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
    },
    {
      text: 'Last year',
      value: [
        dayjs().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
        dayjs().subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
      ],
    },
    {
      text: 'This month',
      value: [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
    },
    {
      text: 'Last month',
      value: [
        dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      ],
    },
    {
      text: 'Last 30 days',
      value: [dayjs().subtract(30, 'days').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
    },
    {
      text: 'Last 7 days',
      value: [dayjs().subtract(7, 'days').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
    },
    {
      text: 'Today',
      value: [dayjs().format('YYYY-MM-DD'), dayjs().add(1, 'day').format('YYYY-MM-DD')],
    },
  ]
}

export type ComparisonRange = {
  from: string
  to: string
}
/**
 * Resolves the comparison ("previous") period for a selected date range.
 * Calendar month and year ranges shift back by one calendar unit (so May 1–31 compares to
 * Apr 1–30 and a year compares to the previous year, leap-year aware via dayjs clamping).
 * Any other range falls back to a window of the same length ending the day before `startDate`.
 */
export const getComparisonRange = (startDate: string, endDate: string): ComparisonRange => {
  const start = dayjs(startDate)
  const end = dayjs(endDate)
  if (start.isSame(start.startOf('month'), 'day') && start.isSame(end, 'month')) {
    return {
      from: start.subtract(1, 'month').format('YYYY-MM-DD'),
      to: end.subtract(1, 'month').format('YYYY-MM-DD'),
    }
  }
  if (start.isSame(start.startOf('year'), 'day') && start.isSame(end, 'year')) {
    return {
      from: start.subtract(1, 'year').format('YYYY-MM-DD'),
      to: end.subtract(1, 'year').format('YYYY-MM-DD'),
    }
  }
  const length = end.diff(start, 'day')
  const previousEnd = start.subtract(1, 'day')
  return {
    from: previousEnd.subtract(length, 'day').format('YYYY-MM-DD'),
    to: previousEnd.format('YYYY-MM-DD'),
  }
}
const DEFAULT_RANGE_MAX_DAYS = 365

export const getDefaultRangeOption = (startDate?: string | Date, endDate?: string | Date) => {
  const today = dayjs()
  const campaignEnd = endDate ? dayjs(endDate) : undefined
  const effectiveEnd = campaignEnd?.isBefore(today, 'day') ? campaignEnd : today

  if (!startDate) {
    return {
      startDate: effectiveEnd.startOf('year').format('YYYY-MM-DD'),
      endDate: effectiveEnd.format('YYYY-MM-DD'),
    }
  }
  const earliestStart = effectiveEnd.subtract(DEFAULT_RANGE_MAX_DAYS, 'day')
  const start = dayjs(startDate).isBefore(earliestStart, 'day') ? earliestStart : dayjs(startDate)
  return {
    startDate: (start.isAfter(effectiveEnd, 'day') ? effectiveEnd : start).format('YYYY-MM-DD'),
    endDate: effectiveEnd.format('YYYY-MM-DD'),
  }
}

/** A full calendar year, inclusive of a possible Feb 29 (leap year). */
export const MAX_DATE_RANGE_DAYS = 366

/** Three full calendar years, matching the finance-service campaign performance API limit. */
export const CAMPAIGN_PERFORMANCE_MAX_DATE_RANGE_DAYS = 3 * MAX_DATE_RANGE_DAYS

/**
 * Whether the span between two dates is within `maxDays`, inclusive. Uses a real
 * calendar-day diff (via dayjs), so leap years are handled correctly without any
 * special-casing.
 */
export const isDateRangeWithinLimit = (
  startDate: string | Date,
  endDate: string | Date,
  maxDays: number = MAX_DATE_RANGE_DAYS,
): boolean => Math.abs(dayjs(endDate).diff(dayjs(startDate), 'day')) <= maxDays

export const getLastThirtyDaysRange = () => {
  const now = dayjs()

  return {
    dateFrom: now.subtract(30, 'days').format('YYYY-MM-DD'),
    dateTo: now.format('YYYY-MM-DD'),
  }
}

/**
 * Resolves the display label for a date range: the matching shortcut's text (e.g. "This month")
 * when the range equals a predefined shortcut, otherwise the formatted range, otherwise `emptyLabel`.
 */
export const getDateRangeLabel = (
  startDate?: string,
  endDate?: string,
  options: { emptyLabel?: string } = {},
): string => {
  if (!startDate || !endDate) {
    return options.emptyLabel ?? 'Dates'
  }
  const shortcut = shortcutsDatePickerOptions().find(
    (item) => item.value[0] === startDate && item.value[1] === endDate,
  )
  if (shortcut) {
    return shortcut.text
  }
  return formatDateRangeShort(startDate, endDate)
}
